import { createTablistTokens } from '../tokens/components.js';
import { SEMANTIC, type SemanticTokens } from '../tokens/semantic.js';

/**
 * Tabs Component Configuration
 *
 * Based on Figma design: Custom "card-style" tabs with rounded top corners
 * - Active tab: white background, borders on 3 sides (top, left, right), dark text
 * - Inactive tabs: dark background, bottom border only, white text
 * - Font: Poppins SemiBold, 16px, line-height 24px
 * - Padding: 16px
 * - Border radius: 8px (top corners only)
 */
export function createTabs(s: SemanticTokens) {
  const tablistTokens = createTablistTokens(s);
  return {
    tablist: {
      background: tablistTokens.bg,
      borderWidth: '0',
      borderColor: 'transparent',
    },
    tab: {
      background: tablistTokens.bg,
      hoverBackground: tablistTokens.bgHover,
      activeBackground: tablistTokens.bgActive,
      borderWidth: '1px',
      borderColor: tablistTokens.border,
      hoverBorderColor: tablistTokens.border,
      activeBorderColor: tablistTokens.border,
      color: tablistTokens.fg,
      hoverColor: tablistTokens.fgActive,
      activeColor: tablistTokens.fgActive,
      padding: tablistTokens.base.paddingTab,
      fontWeight: tablistTokens.fontWeight,
      focusRing: { width: '0', style: 'none', color: 'transparent', offset: '0', shadow: 'none' },
    },
    tabpanel: {
      background: tablistTokens.bgActive,
      color: tablistTokens.fgActive,
      padding: tablistTokens.base.paddingPanel,
      focusRing: { width: '0', style: 'none', color: 'transparent', offset: '0', shadow: 'none' },
    },
    navButton: {
      background: tablistTokens.bg,
      color: tablistTokens.fg,
      width: tablistTokens.navButton.width,
      focusRing: { width: '0', style: 'none', color: 'transparent', offset: '0', shadow: 'none' },
      shadow: 'none',
    },
    css: () => `
    .p-tabs {
      min-width: 0;
      max-width: 100%;
    }

    .p-tablist {
      border-radius: ${tablistTokens.base.radius} ${tablistTokens.base.radius} 0 0;
      min-width: 0;
      max-width: 100%;
      width: 100%;
    }

    .p-tablist-content {
      border-radius: ${tablistTokens.base.radius} ${tablistTokens.base.radius} 0 0;
      flex: 1 1 0;
      min-width: 0;
      max-width: 100%;
      width: 0;
    }
    
    .p-tablist-tab-list {
      border-radius: ${tablistTokens.base.radius} ${tablistTokens.base.radius} 0 0;
      padding: 0;
      gap: 0;
    }
    
    .p-tab {
      font-family: ${tablistTokens.fontFamily};
      font-size: ${tablistTokens.fontSize};
      line-height: ${tablistTokens.lineHeight};
      font-weight: ${tablistTokens.fontWeight};
      transition: background-color ${tablistTokens.transitionDuration} cubic-bezier(0.4, 0, 0.2, 1),
                  color ${tablistTokens.transitionDuration} cubic-bezier(0.4, 0, 0.2, 1),
                  border-color ${tablistTokens.transitionDuration} cubic-bezier(0.4, 0, 0.2, 1);
      margin: 0;
      background: ${tablistTokens.bg};
      border: 1px solid transparent;
      border-bottom-color: ${tablistTokens.border};
      border-radius: ${tablistTokens.base.radius} ${tablistTokens.base.radius} 0 0;
      max-width: 14rem;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .p-tab-active,
    .p-tab[aria-selected="true"] {
      background: ${tablistTokens.bgActive};
      color: ${tablistTokens.fgActive};
      border-color: ${tablistTokens.border};
      border-bottom-color: transparent;
      border-radius: ${tablistTokens.base.radius} ${tablistTokens.base.radius} 0 0;
    }

    .p-tab:not(.p-tab-active):hover,
    .p-tab:not(.p-tab-active):not(.p-disabled):hover {
      background: ${tablistTokens.bgHover};
      color: ${tablistTokens.fgActive};
      border-color: transparent;
      border-bottom-color: ${tablistTokens.border};
      border-radius: ${tablistTokens.base.radius} ${tablistTokens.base.radius} 0 0;
    }
    
    .p-tablist-active-bar {
      display: none;
    }

    @media (min-width: 768px) {
      .p-tabs .p-tablist-content {
        scrollbar-width: none !important;
        scrollbar-color: transparent transparent !important;
      }

      .p-tabs .p-tablist-content::-webkit-scrollbar {
        width: 0 !important;
        height: 0 !important;
        display: none !important;
      }
    }
  `,
  };
}
export const tabs = createTabs(SEMANTIC);
